/**
 * 宿主模板的机读键 → 客户看得懂的中文(A.57:从老分支 `features/ai-website/ui/AiWebsitePage.tsx` 搬来的
 * `PAGE_CN / BLOCK_CN / FIELD_CN` 三张词表,词条原样;另补节点角色 / 出处 / 发布体检的人话)。
 * 未收录的键原样显示,不给客户看空。
 */
import type { NodeRole, PreflightError } from "../../../api/types";
import { parseToken } from "../../../model/hostNodes";

export const PAGE_CN: Record<string, string> = {
  home: "首页",
  about: "关于我们",
  aboutus: "关于我们",
  contact: "联系我们",
  products: "产品",
  product: "产品",
  news: "资讯动态",
  articles: "资讯",
  article: "资讯",
  service: "服务",
  services: "服务",
  activity: "活动",
  rooms: "客房",
  features: "设施服务",
  dining: "餐饮",
  careers: "招聘",
  brand: "品牌故事",
  brandstory: "品牌故事",
  culture: "企业文化",
  quality: "品质保证",
  franchise: "加盟",
  install: "安装服务",
  shop: "门店",
  store: "门店",
  stores: "门店",
  solution: "解决方案",
  case: "案例",
  honor: "荣誉",
  technology: "技术",
  team: "团队",
};

export const BLOCK_CN: Record<string, string> = {
  navbar: "导航栏",
  herosection: "首屏主视觉",
  hero: "首屏",
  banner: "横幅",
  footer: "页脚",
  text: "文本区",
  featureicons: "卖点/承诺",
  features: "卖点/承诺",
  feature: "卖点",
  button: "按钮区",
  image: "图片",
  images: "图片组",
  carousel: "轮播图",
  articlList: "资讯列表",
  articlelist: "资讯列表",
  productgrid: "产品列表",
  productGrid: "产品列表",
  brandlogos: "品牌轮播",
  testimonials: "客户评价",
  beforeafter: "效果对比",
  companyinfo: "企业信息",
  cardgrid: "卡片区",
  formsubmit: "提交表单",
  tab: "标签页",
  contact: "联系区",
  menu: "菜单区",
  title: "标题区",
  list: "列表区",
  stats: "数据区",
};

export const FIELD_CN: Record<string, string> = {
  siteName: "网站名称",
  companyName: "公司名称",
  title: "标题",
  subtitle: "副标题",
  description: "描述",
  content: "文字内容",
  slogan: "标语",
  icp: "ICP 备案号",
  copyright: "版权信息",
  address: "地址",
  phone: "电话",
  email: "邮箱",
  wechat: "微信",
  link: "链接",
  url: "链接",
  buttonText: "按钮文字",
  button: "按钮",
  logo: "Logo",
  image: "图片",
  src: "图片",
  backgroundImage: "背景图",
  icon: "图标",
  bgColor: "背景颜色",
  color: "颜色",
  textColor: "文字颜色",
  activeColor: "高亮颜色",
  borderColor: "边框颜色",
  menuStyle: "菜单样式",
  align: "对齐方式",
  fontSize: "字号",
  size: "尺寸",
  width: "宽度",
  height: "高度",
  gap: "间距",
  spacing: "间距",
  padding: "内边距",
  margin: "外边距",
  bold: "加粗",
  weight: "字重",
  name: "名称",
  item: "内容项",
  items: "内容项",
  label: "标签",
  text: "文字",
  date: "日期",
  time: "时间",
  star: "星级",
  category: "分类",
  tag: "标签",
  tags: "标签",
  theme: "主题",
};

/** 数组项字段:menus[0].label / menus.0.label / items[2].image —— 带序号以免多条同名分不清。 */
const ARRAY_BASE_CN: Record<string, string> = {
  menus: "菜单项",
  items: "条目",
  slides: "轮播图",
  columns: "栏目",
  tabs: "标签页",
  links: "链接",
};

const ARRAY_SUB_CN: Record<string, string> = {
  label: "文字",
  link: "链接",
  title: "标题",
  text: "说明",
  desc: "描述",
  description: "描述",
  image: "图片",
  src: "图片",
  url: "链接",
  icon: "图标",
};

/** 页键 → 中文(未收录原样;空键 = token 解析不了的归堆)。 */
export function pageCn(p: string): string {
  if (!p) return "其他";
  return PAGE_CN[p] ?? p;
}

/** block 键(去尾部序号,如 navbar0)→ 中文。 */
export function blockCn(b: string): string {
  const base = b.replace(/\d+$/, "");
  return BLOCK_CN[base] ?? BLOCK_CN[b] ?? b;
}

/** 字段键(可带层级,如 siteName.content / items.0.title)→ 中文;未收录按首段原样兜底。 */
export function fieldCn(f: string): string {
  const arr = f.match(/^([a-zA-Z_]+)[.[](\d+)[.\]]\.?(.*)$/);
  if (arr) {
    const base = ARRAY_BASE_CN[arr[1].toLowerCase()] ?? arr[1];
    const idx = Number(arr[2]) + 1;
    const sub = ARRAY_SUB_CN[(arr[3] || "").split(".")[0].toLowerCase()] ?? arr[3] ?? "";
    return sub ? `${base} ${idx} · ${sub}` : `${base} ${idx}`;
  }
  const seg = f.split(".");
  const hit = FIELD_CN[seg[0]];
  if (hit) return hit;
  if (seg.length > 1 && FIELD_CN[seg[seg.length - 1]]) return FIELD_CN[seg[seg.length - 1]];
  return seg[0];
}

/** token → 「首页 · 导航栏 · 网站名称」;解析失败原样返回。 */
export function displayToken(token: string): string {
  const p = parseToken(token);
  if (!p) return token;
  return `${pageCn(p.page)} · ${blockCn(p.block)} · ${fieldCn(p.field)}`;
}

/** 节点角色 → 中文(编辑页节点行的前缀)。 */
export const ROLE_CN: Record<NodeRole, string> = {
  color: "颜色",
  dynamic: "产品数据",
  nav: "导航",
  link: "链接",
  "img-logo": "Logo",
  "img-hero": "首屏图",
  "img-item": "配图",
  alt: "图片说明",
  slot: "卡片文字",
  "fact-company": "公司名",
  "fact-address": "地址",
  "fact-phone": "电话",
  "fact-email": "邮箱",
  "fact-icp": "备案号",
  copyright: "版权行",
  ui: "界面文字",
  empty: "空白",
  para: "段落",
  heading: "标题",
};

export function roleCn(role: string): string {
  return (ROLE_CN as Record<string, string>)[role] ?? role;
}

/**
 * 节点出处 → 标签(A.57 / 任务书):`kb:` →「资料」,`user:` →「你填写的」,`ai:` →「AI 起草」;认不出不画。
 * (stub 五屏的字段来源 chip 是「来自资料」,见 model/content 的 bindingSourceText,两处各管各的页面。)
 */
export function nodeSourceLabel(binding: string | null | undefined): string | null {
  if (!binding) return null;
  if (binding.startsWith("kb:")) return "资料";
  if (binding.startsWith("user:")) return "你填写的";
  if (binding.startsWith("ai:")) return "AI 起草";
  return null;
}

/** 发布体检一条 error → 「首页 · 文本区 · 节点:原因」(token 缺就用 page / path)。 */
export function preflightErrorText(e: PreflightError): string {
  const where = e.token
    ? `${pageCn(parseToken(e.token)?.page ?? e.page ?? "")} · ${blockCn(parseToken(e.token)?.block ?? "")} · 节点`
    : e.page
      ? pageCn(e.page)
      : (e.path ?? "");
  return where ? `${where}:${e.reason}` : e.reason;
}

const WARNING_CODE_CN: Record<string, string> = {
  TEMPLATE_IMAGE_CLEARED: "模板自带图片会被清空",
  TEMPLATE_LINK_CLEARED: "模板自带外链会被清空",
  HOST_PRODUCTS_UNAVAILABLE: "宿主暂未开放产品接口,产品没有同步",
  // 出稿(移植)回包的码(1009 C 线:初稿 toast 也过这张表,不再原样露码)
  TEMPLATE_FACT_CLEARED: "没有对应资料,这一处已清空",
  TRANSPLANT_TOKEN_MISSING: "AI 没改写这一处,暂留模板原文(含原企业名的发布前会拦)",
  TEXTS_REWRITE_FAILED:
    "导航与按钮文字这次没能按你的业务改写,沿用模板原文 —— 可在编辑页「导航与按钮」里手动改,或重新生成初稿",
};

/**
 * 发布体检 / 发布回包的 warning → 人话。api 的 warning 文字形状还没钉(A.51 只给了码),这里认
 * 「码 + 空白/冒号 + token」:码换中文、token 换「页 · 块 · 字段」;认不出的整句原样显示。
 */
export function preflightWarningText(w: string): string {
  const m = /^([A-Z_]+)(?:[\s:：]+(.*))?$/s.exec(w.trim());
  if (!m || !WARNING_CODE_CN[m[1]]) return w;
  const rest = (m[2] ?? "").trim();
  const where = /^\{\{site\..+\}\}$/.test(rest) ? displayToken(rest) : rest;
  return where ? `${WARNING_CODE_CN[m[1]]}:${where}` : WARNING_CODE_CN[m[1]];
}

/** 独立图片块 / 空图片格的占位字(与宿主清空后显示的同一句)。 */
export const NO_IMAGE_TEXT = "暂无图片";

/** 节点行里值的预览长度(任务书:值前 20 字)。 */
const PREVIEW_LEN = 20;

export function previewText(value: string): string {
  const flat = value.replace(/\s+/g, " ").trim();
  if (!flat) return "(空)";
  return flat.length > PREVIEW_LEN ? `${flat.slice(0, PREVIEW_LEN)}…` : flat;
}

/** 1009 图片位的标签:「关于我们 · 头图」「首页 · 图片 3」「首页 · 品牌轮播 · 图片 2」。`index` = 页内配图序号。 */
export function imageSlotLabel(slot: {
  page: string;
  block: string;
  field: string;
  role: string;
  index?: number;
}): string {
  const page = pageCn(slot.page);
  if (slot.role === "img-logo") return `${page} · Logo`;
  if (slot.role === "img-hero") return `${page} · 头图`;
  const m = /^(?:items|slides)[.[](\d+)/.exec(slot.field);
  if (m) return `${page} · ${blockCn(slot.block)} · 图片 ${Number(m[1]) + 1}`;
  return `${page} · 图片 ${slot.index ?? 1}`;
}

/** 没单独换图时这一格跟着谁(`defaultSource`)→ 人话;`productName` 给产品图带名字。 */
export function defaultSourceText(source: string | null, productName?: string): string | null {
  if (!source) return null;
  if (source === "logo") return "跟随 Logo";
  if (source === "hero") return "跟随首页首图";
  const m = /^product:(\d+)$/.exec(source);
  if (m) return `跟随产品图 · ${productName || `产品 ${Number(m[1]) + 1}`}`;
  return null;
}
